import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/contexts/auth-context';
import { PropertyProvider } from '@/contexts/property-context';
import { ThemeProvider } from '@/components/theme-provider';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { Shell } from '@/components/layouts/Shell';
import { PublicLayout } from '@/components/layouts/PublicLayout';
import { RequireModule } from '@/components/common/RequireModule';
import { RequireStaff } from '@/components/common/RequireStaff';
import { RequireContractor } from '@/components/common/RequireContractor';
import { useSuperAdminSettings } from '@/hooks/use-superadmin';
import { ConsentBanner } from '@/components/common/ConsentBanner';
import { AnalyticsPageView } from '@/components/common/AnalyticsPageView';

// Route-level code splitting with React.lazy
const HomePage = React.lazy(() => import('@/pages/HomePage').then(m => ({ default: m.HomePage })));
const LandingPage = React.lazy(() => import('@/pages/landing/LandingPage').then(m => ({ default: m.LandingPage })));
const PropertiesDirectoryPage = React.lazy(() => import('@/pages/properties/PropertiesDirectoryPage').then(m => ({ default: m.PropertiesDirectoryPage })));
const UnitsPage = React.lazy(() => import('@/pages/UnitsPage').then(m => ({ default: m.UnitsPage })));
const ResidentsPage = React.lazy(() => import('@/pages/ResidentsPage').then(m => ({ default: m.ResidentsPage })));
const MaintenancePage = React.lazy(() => import('@/pages/MaintenancePage').then(m => ({ default: m.MaintenancePage })));
const MaintenanceTicketDetailPage = React.lazy(() => import('@/pages/maintenance/MaintenanceTicketDetailPage').then(m => ({ default: m.MaintenanceTicketDetailPage })));
const VehiclesPage = React.lazy(() => import('@/pages/vehicles/VehiclesPage').then(m => ({ default: m.VehiclesPage })));
const TransactionsPage = React.lazy(() => import('@/pages/TransactionsPage').then(m => ({ default: m.TransactionsPage })));
const BankStatementsPage = React.lazy(() => import('@/pages/finance/BankStatementsPage').then(m => ({ default: m.BankStatementsPage })));
const FinancialOverviewPage = React.lazy(() => import('@/pages/finance/FinancialOverviewPage').then(m => ({ default: m.FinancialOverviewPage })));
const ReportsPage = React.lazy(() => import('@/pages/finance/ReportsPage').then(m => ({ default: m.ReportsPage })));
const SettingsPage = React.lazy(() => import('@/pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const SecuritySettingsPage = React.lazy(() => import('@/pages/settings/SecuritySettingsPage').then(m => ({ default: m.SecuritySettingsPage })));
const NoticesPage = React.lazy(() => import('@/pages/community/NoticesPage').then(m => ({ default: m.NoticesPage })));
const AmenitiesPage = React.lazy(() => import('@/pages/community/AmenitiesPage').then(m => ({ default: m.AmenitiesPage })));
const PollsPage = React.lazy(() => import('@/pages/community/PollsPage').then(m => ({ default: m.PollsPage })));
const LoginPage = React.lazy(() => import('@/pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = React.lazy(() => import('@/pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = React.lazy(() => import('@/pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = React.lazy(() => import('@/pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const ActivateAccountPage = React.lazy(() => import('@/pages/auth/ActivateAccountPage').then(m => ({ default: m.ActivateAccountPage })));
const SuperAdminShell = React.lazy(() => import('@/components/layouts/SuperAdminShell').then(m => ({ default: m.SuperAdminShell })));
const OverviewPage = React.lazy(() => import('@/pages/superadmin/OverviewPage').then(m => ({ default: m.OverviewPage })));
const WorkspacesPage = React.lazy(() => import('@/pages/superadmin/WorkspacesPage').then(m => ({ default: m.WorkspacesPage })));
const UsersPage = React.lazy(() => import('@/pages/superadmin/UsersPage').then(m => ({ default: m.UsersPage })));
const SuperAdminContractorsPage = React.lazy(() => import('@/pages/superadmin/SuperAdminContractorsPage').then(m => ({ default: m.SuperAdminContractorsPage })));
const BroadcastsPage = React.lazy(() => import('@/pages/superadmin/BroadcastsPage').then(m => ({ default: m.BroadcastsPage })));
const SuperAdminReportsPage = React.lazy(() => import('@/pages/superadmin/ReportsPage').then(m => ({ default: m.ReportsPage })));
const AuditLogPage = React.lazy(() => import('@/pages/superadmin/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const SuperAdminSettingsPage = React.lazy(() => import('@/pages/superadmin/SettingsPage').then(m => ({ default: m.SettingsPage })));
const CompanyProfilePage = React.lazy(() => import('@/pages/company/CompanyProfilePage').then(m => ({ default: m.CompanyProfilePage })));
const WorkspaceTeamPage = React.lazy(() => import('@/pages/workspace/WorkspaceTeamPage').then(m => ({ default: m.WorkspaceTeamPage })));
const PropertyProfilePage = React.lazy(() => import('@/pages/properties/PropertyProfilePage').then(m => ({ default: m.PropertyProfilePage })));
const PropertyActionCenterPage = React.lazy(() => import('@/pages/properties/PropertyActionCenterPage').then(m => ({ default: m.PropertyActionCenterPage })));
const VendorsPage = React.lazy(() => import('@/pages/properties/VendorsPage').then(m => ({ default: m.VendorsPage })));
const PropertyExpensesConfigPage = React.lazy(() => import('@/pages/properties/PropertyExpensesConfigPage').then(m => ({ default: m.PropertyExpensesConfigPage })));
const ResidentVehiclesPage = React.lazy(() => import('@/pages/portal/ResidentVehiclesPage').then(m => ({ default: m.ResidentVehiclesPage })));
const TenancyPage = React.lazy(() => import('@/pages/portal/TenancyPage').then(m => ({ default: m.TenancyPage })));
const DocumentsPage = React.lazy(() => import('@/pages/documents/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const DocsShell = React.lazy(() => import('@/components/docs/DocsShell').then(m => ({ default: m.DocsShell })));
const DocsArticlePage = React.lazy(() => import('@/pages/docs/DocsArticlePage').then(m => ({ default: m.DocsArticlePage })));
const PrivacyNoticePage = React.lazy(() => import('@/pages/legal/PrivacyNoticePage').then(m => ({ default: m.PrivacyNoticePage })));
const TermsPage = React.lazy(() => import('@/pages/legal/TermsPage').then(m => ({ default: m.TermsPage })));
const CalculatorPage = React.lazy(() => import('@/pages/calculator/CalculatorPage').then(m => ({ default: m.CalculatorPage })));

// Contractor Portal Pages
const ContractorDashboardPage = React.lazy(() => import('@/pages/contractor/ContractorDashboardPage').then(m => ({ default: m.ContractorDashboardPage })));
const ContractorJobsPage = React.lazy(() => import('@/pages/contractor/ContractorJobsPage').then(m => ({ default: m.ContractorJobsPage })));
const ContractorJobDetailPage = React.lazy(() => import('@/pages/contractor/ContractorJobDetailPage').then(m => ({ default: m.ContractorJobDetailPage })));
const ContractorSchedulePage = React.lazy(() => import('@/pages/contractor/ContractorSchedulePage').then(m => ({ default: m.ContractorSchedulePage })));
const ContractorInvoicesPage = React.lazy(() => import('@/pages/contractor/ContractorInvoicesPage').then(m => ({ default: m.ContractorInvoicesPage })));
const ContractorTeamPage = React.lazy(() => import('@/pages/contractor/ContractorTeamPage').then(m => ({ default: m.ContractorTeamPage })));
const ContractorProfilePage = React.lazy(() => import('@/pages/contractor/ContractorProfilePage').then(m => ({ default: m.ContractorProfilePage })));

const PageLoadingFallback: React.FC = () => (
  <div className="flex h-64 w-full items-center justify-center">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

const RequireAuditTrail: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: settings, isLoading } = useSuperAdminSettings();
  if (isLoading) return <PageLoadingFallback />;
  // Redirect unless the trail is positively known to be on. Checking for false
  // only would render the page during the settings request, which fires the
  // audit query and produces a 403 the guard was meant to prevent.
  if (!settings?.audit_trail_enabled) {
    return <Navigate to="/superadmin/settings" replace />;
  }
  return <>{children}</>;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
  },
});

export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <PropertyProvider>
          <BrowserRouter>
              <React.Suspense fallback={<PageLoadingFallback />}>
                <Routes>
              {/* Public Marketing & Auth Routes (Shared Navbar & Footer) */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/activate" element={<ActivateAccountPage />} />
                <Route path="/privacy" element={<PrivacyNoticePage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/calculator" element={<CalculatorPage />} />
              </Route>

              {/* Documentation - requires authentication */}
              <Route element={<ProtectedRoute><DocsShell /></ProtectedRoute>}>
                <Route path="/docs" element={<DocsArticlePage />} />
                <Route path="/docs/:slug" element={<DocsArticlePage />} />
              </Route>

              {/* Authenticated Application Shell */}
              <Route
                element={
                  <ProtectedRoute>
                    <Shell />
                  </ProtectedRoute>
                }
              >
                <Route path="/app" element={<HomePage />} />
                {/* Per-property resident dashboard is the resident half of /units */}
                <Route
                  path="company-profile"
                  element={
                    <RequireStaff>
                      <CompanyProfilePage />
                    </RequireStaff>
                  }
                />
                <Route
                  path="workspace/team"
                  element={
                    <RequireStaff>
                      <WorkspaceTeamPage />
                    </RequireStaff>
                  }
                />
                <Route
                  path="property/profile"
                  element={
                    <RequireStaff>
                      <PropertyProfilePage />
                    </RequireStaff>
                  }
                />
                <Route
                  path="property/action-center"
                  element={
                    <RequireStaff>
                      <PropertyActionCenterPage />
                    </RequireStaff>
                  }
                />
                <Route
                  path="property/vendors"
                  element={
                    <RequireModule module="vendors" feature="Vendors & Contractors">
                      <VendorsPage />
                    </RequireModule>
                  }
                />
                <Route
                  path="notices"
                  element={
                    <RequireModule module="amenities" feature="Notices">
                      <NoticesPage />
                    </RequireModule>
                  }
                />
                <Route
                  path="amenities"
                  element={
                    <RequireModule module="amenities" feature="Amenities">
                      <AmenitiesPage />
                    </RequireModule>
                  }
                />
                <Route
                  path="polls"
                  element={
                    <RequireModule module="voting" feature="Polls">
                      <PollsPage />
                    </RequireModule>
                  }
                />
                <Route
                  path="properties"
                  element={
                    <RequireStaff>
                      <PropertiesDirectoryPage />
                    </RequireStaff>
                  }
                />
                <Route path="units" element={<UnitsPage />} />
                {/* Dedicated tenancy surface (Area 3 D6): the property module is
                    the master switch, the unit switch gates it inside the page. */}
                <Route
                  path="tenancy"
                  element={
                    <RequireModule module="tenancy_lease" feature="Tenancy">
                      <TenancyPage />
                    </RequireModule>
                  }
                />
                <Route
                  path="residents"
                  element={
                    <RequireStaff>
                      <ResidentsPage />
                    </RequireStaff>
                  }
                />
                <Route
                  path="documents"
                  element={
                    <RequireModule module="documents" feature="Document Storage & Sharing">
                      <DocumentsPage />
                    </RequireModule>
                  }
                />
                <Route
                  path="vehicles"
                  element={
                    <RequireStaff>
                      <RequireModule module="vehicles" feature="Vehicles & Parking">
                        <VehiclesPage />
                      </RequireModule>
                    </RequireStaff>
                  }
                />
                <Route
                  path="my-vehicles"
                  element={
                    <RequireModule module="vehicles" feature="Vehicles & Parking">
                      <ResidentVehiclesPage />
                    </RequireModule>
                  }
                />
                <Route
                  path="property/expenses/config"
                  element={
                    <RequireStaff>
                      <PropertyExpensesConfigPage />
                    </RequireStaff>
                  }
                />
                <Route
                  path="import-statements"
                  element={
                    <RequireStaff>
                      <RequireModule module="bank_reconciliation" feature="Import Statements">
                        <BankStatementsPage />
                      </RequireModule>
                    </RequireStaff>
                  }
                />
                <Route path="financial-overview" element={<FinancialOverviewPage />} />
                <Route
                  path="reports"
                  element={
                    <RequireModule module="reports" feature="Reports">
                      <ReportsPage />
                    </RequireModule>
                  }
                />
                <Route
                  path="transactions"
                  element={
                    <RequireStaff>
                      <TransactionsPage />
                    </RequireStaff>
                  }
                />
                <Route
                  path="maintenance"
                  element={
                    <RequireModule module="maintenance" feature="Maintenance">
                      <MaintenancePage />
                    </RequireModule>
                  }
                />
                <Route
                  path="maintenance/:ticketId"
                  element={
                    <RequireModule module="maintenance" feature="Maintenance">
                      <MaintenanceTicketDetailPage />
                    </RequireModule>
                  }
                />

                <Route path="settings" element={<SettingsPage />} />
                <Route path="settings/security" element={<SecuritySettingsPage />} />

                {/* Contractor Portal Routes */}
                <Route path="contractor/dashboard" element={<RequireContractor><ContractorDashboardPage /></RequireContractor>} />
                <Route path="contractor/jobs" element={<RequireContractor><ContractorJobsPage /></RequireContractor>} />
                <Route path="contractor/jobs/:ticketId" element={<RequireContractor><ContractorJobDetailPage /></RequireContractor>} />
                <Route path="contractor/schedule" element={<RequireContractor><ContractorSchedulePage /></RequireContractor>} />
                <Route path="contractor/invoices" element={<RequireContractor><ContractorInvoicesPage /></RequireContractor>} />
                <Route path="contractor/team" element={<RequireContractor><ContractorTeamPage /></RequireContractor>} />
                <Route path="contractor/profile" element={<RequireContractor><ContractorProfilePage /></RequireContractor>} />
              </Route>

              {/* Super Admin Shell (separate layout) */}
              <Route
                path="/superadmin"
                element={
                  <ProtectedRoute>
                    <SuperAdminShell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<OverviewPage />} />
                <Route path="workspaces" element={<WorkspacesPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="contractors" element={<SuperAdminContractorsPage />} />
                <Route path="reports" element={<SuperAdminReportsPage />} />
                <Route path="broadcasts" element={<BroadcastsPage />} />
                <Route
                  path="audit"
                  element={
                    <RequireAuditTrail>
                      <AuditLogPage />
                    </RequireAuditTrail>
                  }
                />
                <Route path="settings" element={<SuperAdminSettingsPage />} />
                <Route path="purge" element={<Navigate to="/superadmin/reports?tab=purge" replace />} />
              </Route>






                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </React.Suspense>
            <AnalyticsPageView />
          </BrowserRouter>
          <Toaster position="top-right" theme="dark" richColors />
          <ConsentBanner />
        </PropertyProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);
};
